import {
  BrandContentBriefQuery,
  ContentBriefMediaStatus,
  MediaType,
} from "../../../graphql/generated";
import styled from "../../../styles";
import { formatAspectRatio, getCssAspectRatio } from "../../../utils/content";
import { renderContentBriefMediaStatusTag } from "../../../utils/enums";
import { Button } from "../../CTA";
import { Flex } from "../../Flex";
import { PicturesIcon } from "../../icons/PictureIcon";
import { VideoIcon } from "../../icons/VideoIcon";
import { Text } from "../../Text";
import { SubmissionButtonsHoverOverlay, TagWrap } from "../BookingDetails";

export interface DeliverableItemProps {
  deliverable: BrandContentBriefQuery["contentBrief"]["deliverables"][0];
  setSelectedDeliverableId?: (id: string) => void;
}

const DeliverableWrap = styled.div<{
  aspectRatio: string;
  hideBorder?: boolean;
}>`
  border: ${(p) =>
    p.hideBorder ? "none" : `2px dashed ${p.theme.color.card.divider}`};
  border-radius: ${(p) => p.theme.misc.borderRadiusSmall};
  width: 100%;
  position: relative;
  overflow: hidden;

  aspect-ratio: ${(p) => {
    const [width, height] = p.aspectRatio.split(":").map(Number);
    return `${width} / ${height}`;
  }};
`;

const DeliverableContent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: ${(p) => p.theme.spacing.m} ${(p) => p.theme.spacing.l};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

export const DeliverableItem = ({
  deliverable,
  setSelectedDeliverableId,
}: DeliverableItemProps) => {
  const media = deliverable.media;

  if (media) {
    const isVideo = media.mediaType === MediaType.Video;
    const displayUrl = isVideo ? media.thumbnailUrl : media.mediaUrl;

    return (
      <DeliverableWrap
        aspectRatio={getCssAspectRatio(deliverable.aspectRatio)}
        hideBorder
      >
        <div
          style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}
        >
          <Image src={displayUrl || ""} />
          <TagWrap>
            <div>{renderContentBriefMediaStatusTag(media.status)}</div>
          </TagWrap>
          <SubmissionButtonsHoverOverlay>
            <div
              style={{
                zIndex: 2,
              }}
            >
              {media.status === ContentBriefMediaStatus.Pending ? (
                <Button
                  size="xs"
                  buttonType="primary"
                  onClick={() => {
                    setSelectedDeliverableId?.(deliverable.id);
                  }}
                >
                  Review
                </Button>
              ) : (
                <Button
                  size="xs"
                  buttonType="primary"
                  onClick={() => {
                    window.open(media.mediaUrl, "_blank");
                  }}
                >
                  Download
                </Button>
              )}
            </div>
          </SubmissionButtonsHoverOverlay>
        </div>
      </DeliverableWrap>
    );
  }

  return (
    <DeliverableWrap aspectRatio={getCssAspectRatio(deliverable.aspectRatio)}>
      <DeliverableContent>
        <Flex align="center" direction="column">
          {deliverable.mediaType === MediaType.Video ? (
            <VideoIcon width={26} colorPreset="secondary" />
          ) : (
            <PicturesIcon width={26} colorPreset="secondary" />
          )}
          <Text size="xs" margin="xs 0 0 0" colorPreset="secondary">
            {formatAspectRatio(deliverable.aspectRatio)}
          </Text>
        </Flex>
      </DeliverableContent>
    </DeliverableWrap>
  );
};

const Image = styled.img<{ maxHeight?: number }>`
  max-width: 100%;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
  margin: 0 auto;
  box-shadow: ${(p) => p.theme.shadow.card};
  border-radius: ${(p) => p.theme.misc.borderRadiusSmall};
  max-height: ${(p) => (p.maxHeight ? `${p.maxHeight}px` : "none")};
`;
